import { useCallback, useState } from 'react'
import type { useAgencyFlow } from './agency-flow.hook'
import { getChildNodeIdList, hasChildNodes } from './agency-flow-layout.util'

interface DeleteDialogState {
  isOpen: boolean
  nodeId: string | null
  nodeLabel: string | null
  mode: 'confirm' | 'cannot-delete'
  childNodeIdList: string[]
}

const initialState: DeleteDialogState = {
  isOpen: false,
  nodeId: null,
  nodeLabel: null,
  mode: 'confirm',
  childNodeIdList: [],
}

export const useAgencyFlowDeleteDialog = (agencyFlow: ReturnType<typeof useAgencyFlow>) => {
  const [deleteDialogState, setDeleteDialogState] = useState<DeleteDialogState>(initialState)

  const handleDeleteRequest = useCallback(
    (nodeId: string): void => {
      const node = agencyFlow.nodeList.find((n) => n.id === nodeId)
      if (!node) return
      if (hasChildNodes(nodeId, agencyFlow.edgeList)) {
        setDeleteDialogState({
          isOpen: true,
          nodeId,
          nodeLabel: node.data.label,
          mode: 'cannot-delete',
          childNodeIdList: getChildNodeIdList(nodeId, agencyFlow.edgeList),
        })
        return
      }
      setDeleteDialogState({
        isOpen: true,
        nodeId,
        nodeLabel: node.data.label,
        mode: 'confirm',
        childNodeIdList: [],
      })
    },
    [agencyFlow]
  )

  const handleDeleteConfirm = useCallback((): void => {
    if (deleteDialogState.mode === 'confirm' && deleteDialogState.nodeId)
      agencyFlow.deleteNode(deleteDialogState.nodeId)
    setDeleteDialogState(initialState)
  }, [deleteDialogState, agencyFlow])

  const closeDeleteDialog = useCallback((): void => setDeleteDialogState(initialState), [])

  return { deleteDialogState, handleDeleteRequest, handleDeleteConfirm, closeDeleteDialog }
}
